import { useMutation } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { GatedSessionLookupItem } from "ipctg-inflow-types/InFlowDB"
import { useEffect } from "react"
import { Controller, useForm, useWatch } from "react-hook-form"
import { Trans, useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { GatedLookupSessionsError } from "@/utilities/expectedErrors"
import { requiredRule } from "@/utilities/form"
import useToggle from "@/utilities/hooks/useToggle"
import { stripNonNumerics } from "@/utilities/normalizers"

import NoPhoneBasedSessionModal from "../-components/NoPhoneBasedSessionModal"

export const Route = createFileRoute(
  "/qr/$qrLookupID/gated/$pamID/look-up/challenge"
)({
  staticData: {
    pageName: "Verify Phone Number",
  },
  validateSearch: (search: Record<string, unknown>): { plate: string } => {
    return {
      plate: (search.plate as string) || "",
    }
  },
  component: RouteComponent,
})

interface RegistrationFormSchema {
  phone: string
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { plate } = Route.useSearch()
  const { t } = useTranslation(["gated", "common", "errors"])
  const [showModal, toggleModal] = useToggle(false)

  const form = useForm<RegistrationFormSchema>({
    defaultValues: {
      phone: "",
    },
    mode: "onBlur",
  })

  const phone = useWatch({
    control: form.control,
    name: "phone",
    defaultValue: "",
  })

  const { mutate, isPending, reset } = useMutation({
    mutationFn,
    onSuccess,
    throwOnError: true,
  })

  useEffect(() => {
    form.reset(undefined, { keepValues: true })
  }, [form.formState.isSubmitSuccessful])

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("inlane.lookupChallenge.header", { ns: "gated" })}
      </Heading>
      <Card>
        <Heading level={1} as={2}>
          {t("inlane.lookupChallenge.subhead", { ns: "gated" })}
        </Heading>
        <Text>
          <Trans i18nKey="inlane.lookupChallenge.description" ns="gated">
            If you used a phone number to enter the lot, please use the same
            number here. <strong>We need it to retrieve your session.</strong>
          </Trans>
        </Text>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <fieldset disabled={isPending}>
            <Controller<RegistrationFormSchema>
              name="phone"
              control={form.control}
              rules={{
                required: requiredRule(t("common.required", { ns: "errors" })),
              }}
              render={({
                field: { ref, ...rest },
                fieldState: { error, invalid },
              }) => (
                <TextField {...rest} isInvalid={invalid}>
                  <Label>
                    {t("inlane.lookupChallenge.fieldLabel", { ns: "gated" })}
                  </Label>
                  {/* Thin space cause safari :/ */}
                  <TextField.Phone
                    innerRef={ref}
                    placeholder="​(123) 456-7890"
                  />
                  <FieldError>{error?.message}</FieldError>
                </TextField>
              )}
            />
            <Button type="submit" buttonType="primary" isPending={isPending}>
              {t("common.continue", { ns: "common" })}
              <Icon name="arrowRight" slot="right" />
            </Button>
          </fieldset>
        </form>
      </Card>
      <NoPhoneBasedSessionModal
        isOpen={showModal}
        onOpenChange={toggleModal}
        isInExitLane={true}
        phone={phone}
        resetMutation={reset}
        onExit={() =>
          navigate({
            to: "/qr/$qrLookupID/gated/$pamID/look-up",
            replace: true,
          })
        }
      />
    </>
  )

  function onSubmit(data: RegistrationFormSchema) {
    mutate(data)
  }

  async function mutationFn(data: RegistrationFormSchema) {
    const phone = stripNonNumerics(data.phone)

    const rsp = await api.gated.lookupSessions.POST({
      plate,
      phone,
      method: "phone",
    })

    if ("failReason" in rsp) {
      throw new GatedLookupSessionsError(rsp.failReason)
    }

    const session = rsp.sessions.find((s) => !s.isRegistered)
    return session
  }

  function onSuccess(rsp: GatedSessionLookupItem | undefined) {
    if (!rsp) {
      toggleModal()
      return
    }

    navigate({
      to: "/qr/$qrLookupID/gated/$pamID/$sessionID/cart",
      params: { sessionID: rsp.sessionID },
      search: { from: Route.id },
    })
  }
}
